import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { FaRegTrashAlt } from 'react-icons/fa';
import { toast } from 'react-hot-toast';

import {
  ProductReadModel,
  SubProductCategoryTemplateReadModel,
  SubProductReadModel,
  useGetAgreementsQuery,
  useGetProductsByIdChildrenQuery,
  useGetProductsSubProductTemplatesQuery,
  useGetPricesQuery,
  useDeleteProductsByIdChildrenAndChildIdMutation,
  usePostProductsByIdChildrenMutation,
  usePutProductsByIdChildrenAndChildIdMutation,
} from '@client/api';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyPlaceholder,
  InfoTooltip,
  Input,
  Modal,
  ModalContent,
  ModalFooter,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TableRow,
  Text,
  Typography,
} from '@client/toolkit';

import { SubProductAddModal } from './sub-product-add-modal.component';

export interface SubProductsCardProps {
  product?: ProductReadModel;
  className?: string;
  dimmed?: boolean;
}

export function SubProductsCard({ product, className, dimmed }: SubProductsCardProps) {
  const { t } = useTranslation('products');
  const { t: tCommon } = useTranslation();

  const formatAmount = (child: SubProductReadModel) => {
    if (!child.unitAmount) return '–';
    return `${Math.round(child.unitAmount.amount * 100) / 100} kr`;
  };

  const formatYearlyAmount = (child: SubProductReadModel) => {
    if (!child.unitAmount) return '–';
    return `${Math.round(child.unitAmount.amount * 12 * 100) / 100} kr`;
  };

  const formatRange = (child: SubProductReadModel) => {
    const template = templates?.find((t) => t.name === child.name);
    if (!template) return '–';
    return `${template.benchmarkLow} – ${template.benchmarkHigh}`;
  };

  const { id } = useParams<{ id: string }>();

  const [addingSubProduct, setAddingSubProduct] = useState(false);
  const [showTypeWarning, setShowTypeWarning] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editPrices, setEditPrices] = useState<Record<string, string>>({});
  const [pendingDeletes, setPendingDeletes] = useState<Set<string>>(new Set());

  const isFelleskostnader = product?.category === 'Felleskostnader';

  const { data: subProducts, isLoading, isFetching } = useGetProductsByIdChildrenQuery(
    { id: id ?? '' },
    { skip: id == null || !isFelleskostnader },
  );

  const { data: templates } = useGetProductsSubProductTemplatesQuery(
    { countryCode: 'NO' },
    { skip: !isFelleskostnader },
  );

  const { data: pricesData } = useGetPricesQuery(
    { 'productId.Equal': id ?? '', 'pagination.Limit': 1, 'pagination.Offset': 0 },
    { skip: id == null || !isFelleskostnader },
  );
  const isRecurring = pricesData?.items?.[0]?.recurring ?? true;

  const { data: agreementsData } = useGetAgreementsQuery(
    { 'productId.Equal': id ?? '', 'pagination.Limit': 0, 'pagination.Offset': 0 },
    { skip: id == null },
  );
  const hasAgreements = (agreementsData?.totalCount ?? 0) > 0;

  const [deleteSubProduct] = useDeleteProductsByIdChildrenAndChildIdMutation();
  const [updateSubProduct, { isLoading: isSaving }] = usePutProductsByIdChildrenAndChildIdMutation();
  const [createSubProduct] = usePostProductsByIdChildrenMutation();

  const handleMarkForDelete = useCallback((childId: string) => {
    setPendingDeletes((prev) => {
      const next = new Set(prev);
      next.add(childId);
      return next;
    });
    // Also remove from editPrices so totals update
    setEditPrices((prev) => {
      const next = { ...prev };
      delete next[childId];
      return next;
    });
  }, []);

  // Restore a child that was marked for pending delete. Used when the add-modal
  // sees a name match and wants to "undo" the pending delete instead of creating
  // a duplicate row in the DB.
  const handleUnmarkPendingDelete = useCallback((childId: string) => {
    setPendingDeletes((prev) => {
      if (!prev.has(childId)) return prev;
      const next = new Set(prev);
      next.delete(childId);
      return next;
    });
    // Restore the editPrices entry that handleMarkForDelete cleared, so the
    // totals recompute correctly and the outer Save sends the right price.
    setEditPrices((prev) => {
      if (childId in prev) return prev;
      const child = subProducts?.find((c) => c.id === childId);
      if (!child) return prev;
      return { ...prev, [childId]: String(child.unitAmount?.amount ?? '') };
    });
  }, [subProducts]);

  // Smart add: if a child with this name is currently pending delete, unmark it
  // (undo the pending delete). Otherwise POST a new child with the template's
  // benchmark-average price.
  const handleAddTemplate = useCallback(async (template: SubProductCategoryTemplateReadModel) => {
    if (!id) return;
    const matchingPending = subProducts?.find(
      (c) => pendingDeletes.has(c.id) && c.name === template.name,
    );
    if (matchingPending) {
      handleUnmarkPendingDelete(matchingPending.id);
      return;
    }
    await createSubProduct({
      id,
      body: {
        name: template.name,
        price: {
          unitAmount: {
            amount: template.benchmarkAverage / 12,
            currency: { code: 'NOK' },
          },
          interval: { count: 1, type: 'Month' },
        },
      },
    }).unwrap();
  }, [id, subProducts, pendingDeletes, createSubProduct, handleUnmarkPendingDelete]);

  // Same as handleAddTemplate but for the "custom name" input; price defaults to 0
  // so the user can set it in the outer card.
  const handleAddCustom = useCallback(async (name: string) => {
    if (!id) return;
    const trimmed = name.trim();
    if (!trimmed) return;
    const matchingPending = subProducts?.find(
      (c) => pendingDeletes.has(c.id) && c.name === trimmed,
    );
    if (matchingPending) {
      handleUnmarkPendingDelete(matchingPending.id);
      return;
    }
    await createSubProduct({
      id,
      body: {
        name: trimmed,
        price: {
          unitAmount: { amount: 0, currency: { code: 'NOK' } },
          interval: { count: 1, type: 'Month' },
        },
      },
    }).unwrap();
  }, [id, subProducts, pendingDeletes, createSubProduct, handleUnmarkPendingDelete]);

  const startEditing = useCallback(() => {
    if (!subProducts) return;
    const prices: Record<string, string> = {};
    subProducts.forEach((c) => {
      prices[c.id] = String(c.unitAmount?.amount ?? '');
    });
    setEditPrices(prices);
    setPendingDeletes(new Set());
    setEditing(true);
  }, [subProducts]);

  const cancelEditing = () => {
    setEditing(false);
    setEditPrices({});
    setPendingDeletes(new Set());
  };

  // Sync editPrices when subProducts changes during editing (e.g. after adding new ones)
  useEffect(() => {
    if (!editing || !subProducts) return;
    setEditPrices((prev) => {
      const next = { ...prev };
      subProducts.forEach((c) => {
        if (!(c.id in next)) {
          next[c.id] = String(c.unitAmount?.amount ?? '');
        }
      });
      return next;
    });
  }, [editing, subProducts]);

  const handleSave = async () => {
    if (!id || !subProducts) return;
    try {
      // Delete pending removals
      await Promise.all(
        Array.from(pendingDeletes).map((childId) =>
          deleteSubProduct({ id, childId }).unwrap(),
        ),
      );
      // Update remaining sub-products
      await Promise.all(
        subProducts
          .filter((child) => !pendingDeletes.has(child.id))
          .map((child) =>
            updateSubProduct({
              id,
              childId: child.id,
              body: {
                name: child.name,
                description: child.description,
                price: {
                  unitAmount: {
                    amount: parseFloat(editPrices[child.id]) || 0,
                    currency: child.unitAmount?.currency ?? { code: 'NOK' },
                  },
                  interval: child.interval ?? { count: 1, type: 'Month' },
                },
              },
            }).unwrap(),
          ),
      );
      toast.success(tCommon('saved'));
      setEditing(false);
      setEditPrices({});
      setPendingDeletes(new Set());
    } catch {
      toast.error(tCommon('error'));
    }
  };

  const isDisabled = dimmed || !isFelleskostnader;
  const editDisabledByAgreements = hasAgreements && !isDisabled;

  return (
    <>
      <Card className={`${className ?? ''} ${isDisabled ? 'opacity-40 pointer-events-none' : ''}`.trim()}>
        <CardHeader>
          <CardTitle className="flex-1" variant="dense">
            {t('subProducts.title')}
          </CardTitle>
          <div className="flex gap-2">
            {!editing && (
              editDisabledByAgreements ? (
                <InfoTooltip message={t('product.hasAgreementsEditDisabled')} inline>
                  <span>
                    <Button size="sm" color="primary" disabledVariant="dark" disabled>
                      {tCommon('edit')}
                    </Button>
                  </span>
                </InfoTooltip>
              ) : (
                <Button size="sm" color="primary" onClick={startEditing} disabled={isDisabled}>
                  {tCommon('edit')}
                </Button>
              )
            )}
            {editing && (
              <>
                <Button size="sm" color="primary-dark" onClick={cancelEditing}>
                  {tCommon('cancel')}
                </Button>
                <Button size="sm" color="primary-dark" onClick={() => {
                  if (!isRecurring && (!subProducts || subProducts.filter((c) => !pendingDeletes.has(c.id)).length === 0)) {
                    setShowTypeWarning(true);
                  } else {
                    setAddingSubProduct(true);
                  }
                }}>
                  {tCommon('add')}
                </Button>
                <Button size="sm" color="primary" onClick={handleSave} disabled={isSaving}>
                  {tCommon('save')}
                </Button>
              </>
            )}
          </div>
        </CardHeader>

        <CardContent>
          <Table className="overflow-hidden rounded-md">
            {!isLoading && (!subProducts || subProducts.length === 0) && (
              <caption className="px-4 py-4 [caption-side:bottom]">
                <EmptyPlaceholder>
                  {t('subProducts.empty')}
                </EmptyPlaceholder>
              </caption>
            )}

            <TableHead>
              <TableHeadRow>
                <TableHeadCell>{t('subProducts.name')}</TableHeadCell>
                <TableHeadCell>{t('subProducts.priceMonthly')}</TableHeadCell>
                <TableHeadCell>{t('subProducts.priceYearly')}</TableHeadCell>
                <TableHeadCell>{t('subProducts.benchmarkRange')}</TableHeadCell>
                <TableHeadCell className="w-[1%]" />
              </TableHeadRow>
            </TableHead>

            <TableBody>
              {isLoading &&
                Array.from(Array(3).keys()).map((x) => (
                  <TableRow key={x}>
                    <TableCell loading />
                    <TableCell loading />
                    <TableCell loading />
                    <TableCell loading />
                    <TableCell loading />
                  </TableRow>
                ))}

              {!isLoading &&
                subProducts?.filter((child) => !pendingDeletes.has(child.id)).map((child) => (
                  <TableRow key={child.id} verticalAlign="middle">
                    <TableCell>
                      <Text className="text-xs font-medium whitespace-nowrap">
                        {child.name}
                      </Text>
                    </TableCell>
                    <TableCell>
                      {editing ? (
                        <div className="flex items-center gap-2">
                          <div className="w-[170px]">
                            <Input
                              className="text-xs sm:text-xs"
                              type="number"
                              value={editPrices[child.id] ?? ''}
                              onChange={(e) =>
                                setEditPrices((prev) => ({
                                  ...prev,
                                  [child.id]: e.target.value,
                                }))
                              }
                            />
                          </div>
                          <Text className="text-xs whitespace-nowrap">kr</Text>
                        </div>
                      ) : (
                        <Text className="text-xs font-normal whitespace-nowrap">
                          {formatAmount(child)}
                        </Text>
                      )}
                    </TableCell>
                    <TableCell>
                      <Text className="text-xs font-normal whitespace-nowrap">
                        {editing
                          ? `${Math.round((parseFloat(editPrices[child.id]) || 0) * 12 * 100) / 100} kr`
                          : formatYearlyAmount(child)
                        }
                      </Text>
                    </TableCell>
                    <TableCell>
                      <Text className="text-xs font-normal whitespace-nowrap text-secondary">
                        {formatRange(child)}
                      </Text>
                    </TableCell>
                    <TableCell className="text-right">
                      {editing && (
                        <Button
                          size="xs"
                          color="primary-invert"
                          onClick={() => handleMarkForDelete(child.id)}
                        >
                          <FaRegTrashAlt />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}

              {!isLoading && subProducts && subProducts.filter((c) => !pendingDeletes.has(c.id)).length > 0 && (
                <TableRow>
                  <TableCell>
                    <Text className="text-xs font-semibold whitespace-nowrap">
                      {tCommon('total')}
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Text className="text-xs font-semibold whitespace-nowrap">
                      {editing
                        ? `${Math.round(Object.values(editPrices).reduce((sum, v) => sum + (parseFloat(v) || 0), 0) * 100) / 100} kr`
                        : `${Math.round(subProducts.reduce((sum, c) => sum + (c.unitAmount?.amount ?? 0), 0) * 100) / 100} kr`
                      }
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Text className="text-xs font-semibold whitespace-nowrap">
                      {editing
                        ? `${Math.round(Object.values(editPrices).reduce((sum, v) => sum + (parseFloat(v) || 0), 0) * 12 * 100) / 100} kr`
                        : `${Math.round(subProducts.reduce((sum, c) => sum + (c.unitAmount?.amount ?? 0), 0) * 12 * 100) / 100} kr`
                      }
                    </Text>
                  </TableCell>
                  <TableCell />
                  <TableCell />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Modal open={showTypeWarning} onClose={() => setShowTypeWarning(false)}>
        <ModalContent>
          <div className="uppercase text-xs font-semibold p-4 text-center">
            {t('subProducts.typeChangeWarningTitle')}
          </div>
          <Typography align="center" className="px-4 py-4">
            {t('subProducts.typeChangeWarning')}
          </Typography>
        </ModalContent>
        <ModalFooter className="justify-center">
          <Button color="primary" onClick={() => { setShowTypeWarning(false); setAddingSubProduct(true); }}>
            {tCommon('confirm')}
          </Button>
          <Button color="primary-dark" onClick={() => setShowTypeWarning(false)}>
            {tCommon('cancel')}
          </Button>
        </ModalFooter>
      </Modal>

      <SubProductAddModal
        open={addingSubProduct}
        existingChildren={
          subProducts
            ?.filter((c) => !pendingDeletes.has(c.id))
            .map((c) => ({ id: c.id, name: c.name })) ?? []
        }
        onAddTemplate={handleAddTemplate}
        onAddCustom={handleAddCustom}
        onRemoveChild={handleMarkForDelete}
        onClose={() => setAddingSubProduct(false)}
      />
    </>
  );
}
